import React, { useEffect, useRef, useState } from 'react';
import { API_URL } from '../config/endpoints';
import { RIDER_COLORS, shownName } from '../shared/rider';
import { drawSkierSprite } from './entities/skierSprite';
import skierImage from '../assets/SkiierIdleRestV3.png';

type Rider = { mode: 'generated' | 'masked' | 'public'; name: string; color: number };
type Profile = { rider: Rider; publicRider: { playerId: string; username: string; chroma: number } };

function RiderPreview({ color, name }: { color: number; name: string }) {
    const canvas = useRef<HTMLCanvasElement>(null);
    useEffect(() => {
        const image = new Image();
        image.onload = () => {
            const ctx = canvas.current?.getContext('2d');
            if (!ctx) return;
            ctx.clearRect(0, 0, 200, 180);
            ctx.save();
            ctx.translate(100, 98);
            ctx.scale(1.7, 1.7);
            ctx.shadowColor = RIDER_COLORS[color].hex;
            ctx.shadowBlur = 10;
            drawSkierSprite(ctx, image, 'idleRest', 22, RIDER_COLORS[color].hex);
            ctx.restore();
        };
        image.src = skierImage;
        return () => { image.onload = null; };
    }, [color]);
    return <div className="rider-preview">
        <canvas ref={canvas} width={200} height={180} role="img" aria-label={`${RIDER_COLORS[color].name} skier preview`} />
        <strong style={{ color: RIDER_COLORS[color].hex }}>{name}</strong>
        <span className="rider-you-tag">YOU</span>
    </div>;
}

export default function RiderSettings({ token }: { token: string | null }) {
    const [saved, setSaved] = useState<Profile | null>(null);
    const [draft, setDraft] = useState<Rider | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        let active = true;
        setSaved(null); setDraft(null); setError(''); setNotice('');
        (async () => {
            try {
                const response = await fetch(`${API_URL}/api/me/rider`, { headers: { Authorization: `Bearer ${token}` } });
                if (!response.ok) throw new Error('Could not load your rider.');
                const profile: Profile = await response.json();
                if (active) { setSaved(profile); setDraft(profile.rider); }
            } catch { if (active) setError('Could not load your rider. Please retry.'); }
        })();
        return () => { active = false; };
    }, [token, attempt]);

    const save = async () => {
        if (!draft) return;
        setBusy(true); setError(''); setNotice('');
        try {
            const response = await fetch(`${API_URL}/api/me/rider`, {
                method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ ...draft, name: draft.name.trim() }),
            });
            const body = await response.json();
            if (!response.ok) throw new Error(body.error || 'Could not save your rider. Try again.');
            setSaved(body); setDraft(body.rider);
            setNotice('Rider saved. Your next round will use this name and colour.');
        } catch (caught) { setError(caught instanceof Error ? caught.message : 'Could not save your rider. Try again.'); }
        finally { setBusy(false); }
    };
    const dirty = draft && saved && JSON.stringify(draft) !== JSON.stringify(saved.rider);
    const valid = draft && (draft.name.trim() === '' ? draft.mode === 'generated' : /^[A-Za-z0-9][A-Za-z0-9 _-]{2,13}$/.test(draft.name.trim()));
    const edit = (patch: Partial<Rider>) => { setDraft(value => value && { ...value, ...patch }); setNotice(''); };

    return <section className="rider-settings" aria-labelledby="rider-heading">
        <h3 id="rider-heading">Your rider</h3>
        <p className="auth-subtitle">Choose what other players see. Your sign-in name stays private.</p>
        {error && <p role="alert" className="auth-error">{error}</p>}
        {!draft || !saved ? error
            ? <button className="btn-quiet" onClick={() => setAttempt(value => value + 1)}>Retry rider settings</button>
            : <p role="status">Loading your rider…</p>
            : <>
                <div className="rider-editor">
                    <RiderPreview color={draft.color} name={shownName(saved.publicRider.playerId, draft.mode, draft.name.trim())} />
                    <fieldset disabled={busy} className="rider-fields">
                        <label htmlFor="rider-mode">Shown name</label>
                        <select id="rider-mode" className="field" value={draft.mode} onChange={event => edit({ mode: event.target.value as Rider['mode'], ...(event.target.value === 'generated' ? { name: '' } : {}) })}>
                            <option value="generated">Generated name</option>
                            <option value="masked">Masked name</option>
                            <option value="public">Public nickname</option>
                        </select>
                        {draft.mode !== 'generated' && <>
                            <label htmlFor="rider-name">Your chosen shown name</label>
                            <input id="rider-name" className="field" value={draft.name} maxLength={14} minLength={3}
                                onChange={event => edit({ name: event.target.value })} aria-describedby="rider-name-help" autoComplete="off" />
                            <p id="rider-name-help" className="auth-subtitle">3–14 letters, numbers, spaces, dashes or underscores. {draft.mode === 'masked'
                                ? 'Only the first and last characters are shown. This is masking, not anonymity.'
                                : 'Everyone can see this nickname. Avoid personal or contact details.'}</p>
                        </>}
                        <span id="rider-colour-label">Skier colour</span>
                        <div className="rider-colours" role="group" aria-labelledby="rider-colour-label">
                            {RIDER_COLORS.map((color, index) => <button key={color.name} type="button" aria-label={color.name}
                                aria-pressed={draft.color === index} className="rider-swatch" style={{ backgroundColor: color.hex }}
                                onClick={() => edit({ color: index })}>{draft.color === index ? '✓' : ''}</button>)}
                        </div>
                        <p className="auth-subtitle">{RIDER_COLORS[draft.color].name} · A subtle tint and matching marker. Your YOU tag stays visible.</p>
                    </fieldset>
                </div>
                <div className="settings-row">
                    <button className="btn-settings" disabled={busy || !dirty || !valid} onClick={save}>{busy ? 'Saving rider…' : 'Save rider'}</button>
                    <button className="btn-quiet" disabled={busy || !dirty} onClick={() => { setDraft(saved.rider); setError(''); setNotice(''); }}>Discard changes</button>
                </div>
                <p className="auth-subtitle">Preview only until you save. Saved changes apply when you next join a round.</p>
                {notice && <p role="status" className="auth-notice">{notice}</p>}
            </>}
    </section>;
}
